import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('paytrack_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('paytrack_token');
      localStorage.removeItem('paytrack_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  me: () => api.get('/auth/me'),
};

// Organization APIs
export const organizationAPI = {
  getAll: () => api.get('/organizations'),
  create: (data) => api.post('/organizations', data),
  update: (id, data) => api.put(`/organizations/${id}`, data),
  delete: (id) => api.delete(`/organizations/${id}`),
};

// Vertical APIs
export const verticalAPI = {
  getAll: (organizationId) => api.get('/verticals', { params: { organization_id: organizationId } }),
  create: (data) => api.post('/verticals', data),
  update: (id, data) => api.put(`/verticals/${id}`, data),
  delete: (id) => api.delete(`/verticals/${id}`),
};

// Employee APIs
export const employeeAPI = {
  getAll: (params) => api.get('/employees', { params }),
  getOne: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
  markOnNotice: (id, data) => api.put(`/employees/${id}/notice`, data),
  cancelNotice: (id) => api.put(`/employees/${id}/cancel-notice`),
  rehire: (id) => api.put(`/employees/${id}/rehire`),
  uploadCheque: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/employees/${id}/cancelled-cheque`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getChequeMeta: (id) => api.get(`/employees/${id}/cancelled-cheque/meta`),
  getChequeBlob: (id) => api.get(`/employees/${id}/cancelled-cheque`, { responseType: 'blob' }),
  deleteCheque: (id) => api.delete(`/employees/${id}/cancelled-cheque`),
};

// Settings APIs
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
  getStatuses: () => api.get('/settings/statuses'),
};

// Salary Record APIs
export const salaryAPI = {
  getAll: (params) => api.get('/salary-records', { params }),
  create: (data) => api.post('/salary-records', data),
  updateLOP: (id, data) => api.put(`/salary-records/${id}/lop`, data),
  finalize: (id) => api.put(`/salary-records/${id}/finalize`),
  unfinalize: (id) => api.put(`/salary-records/${id}/unfinalize`),
  markPaid: (id) => api.put(`/salary-records/${id}/pay`),
  reverse: (id) => api.put(`/salary-records/${id}/reverse`),
  delete: (id) => api.delete(`/salary-records/${id}`),
  generateMonthly: (month, year, organizationId) => 
    api.post(`/salary-records/generate-monthly?month=${month}&year=${year}${organizationId ? `&organization_id=${organizationId}` : ''}`),
  bulkCreate: (data) => api.post('/salary-records/bulk-create', data),
  getByOrganization: (orgId, month, year) => 
    api.get(`/salary-records/by-organization/${orgId}?month=${month}&year=${year}`),
  generateResigned: (month, year, organizationId) =>
    api.post(`/salary-records/generate-resigned?month=${month}&year=${year}${organizationId ? `&organization_id=${organizationId}` : ''}`),
  getPreviousMonth: (employeeIds, month, year) =>
    api.get(`/salary-records/previous-month?employee_ids=${employeeIds}&month=${month}&year=${year}`),
  getNoticeSettlement: (employeeId) =>
    api.get(`/salary-records/notice-settlement/${employeeId}`),
  getSettlementHistory: (params = {}) => {
    const q = new URLSearchParams();
    if (params.organization_id) q.set('organization_id', params.organization_id);
    if (params.reason) q.set('reason', params.reason);
    if (params.from_date) q.set('from_date', params.from_date);
    if (params.to_date) q.set('to_date', params.to_date);
    if (params.search) q.set('search', params.search);
    return api.get(`/salary-records/settlement-history?${q.toString()}`);
  },
  getSettlementComments: (recordId) =>
    api.get(`/settlement-comments/${recordId}`),
  addSettlementComment: (recordId, text) =>
    api.post(`/settlement-comments/${recordId}`, { text }),
  updateRetention: (id, amount) =>
    api.put(`/salary-records/${id}/retention-payable`, { retention_payable: amount }),
};

// Increment APIs
export const incrementAPI = {
  getAll: (employeeId) => api.get('/increments', { params: { employee_id: employeeId } }),
  create: (data) => api.post('/increments', data),
  getHistory: (employeeId) => api.get(`/increments/history/${employeeId}`),
};

// Report APIs
export const reportAPI = {
  getRetention: (params) => api.get('/reports/retention', { params }),
  getAnnualBonus: (params) => api.get('/reports/annual-bonus', { params }),
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: (params) => api.get('/dashboard/stats', { params }),
};

// Retention Payment APIs
export const retentionPaymentAPI = {
  getAll: (params) => api.get('/retention-payments', { params }),
  getByPeriod: (period, params = {}) => {
    const q = new URLSearchParams({ period });
    if (params.organization_id) q.set('organization_id', params.organization_id);
    if (params.status) q.set('status', params.status);
    if (params.search) q.set('search', params.search);
    return api.get(`/retention-payments/by-period?${q.toString()}`);
  },
  generate: (period, organizationId) => {
    const q = new URLSearchParams({ period });
    if (organizationId) q.set('organization_id', organizationId);
    return api.post(`/retention-payments/generate?${q.toString()}`);
  },
  create: (data) => api.post('/retention-payments', data),
  finalize: (id) => api.put(`/retention-payments/${id}/finalize`),
  applyProrate: (id) => api.put(`/retention-payments/${id}/apply-prorate`),
  bulkProrate: (period) => api.put(`/retention-payments/bulk-prorate?${new URLSearchParams({ period }).toString()}`),
  markPaid: (id) => api.put(`/retention-payments/${id}/pay`),
  updateAmount: (id, amount) => api.put(`/retention-payments/${id}/update-amount`, { amount }),
  revert: (id) => api.put(`/retention-payments/${id}/revert`),
  bulkFinalize: (period, orgId) => {
    const q = new URLSearchParams({ period });
    if (orgId) q.set('organization_id', orgId);
    return api.put(`/retention-payments/bulk-finalize?${q.toString()}`);
  },
  bulkPay: (period, orgId) => {
    const q = new URLSearchParams({ period });
    if (orgId) q.set('organization_id', orgId);
    return api.put(`/retention-payments/bulk-pay?${q.toString()}`);
  },
  bulkRevert: (period, orgId) => {
    const q = new URLSearchParams({ period });
    if (orgId) q.set('organization_id', orgId);
    return api.put(`/retention-payments/bulk-revert?${q.toString()}`);
  },
  delete: (id) => api.delete(`/retention-payments/${id}`),
};

// Annual Bonus Payout APIs (period-based module)
export const annualBonusAPI = {
  getByPeriod: (period, params = {}) => {
    const q = new URLSearchParams({ period });
    if (params.organization_id) q.set('organization_id', params.organization_id);
    if (params.status) q.set('status', params.status);
    if (params.search) q.set('search', params.search);
    return api.get(`/annual-bonus/by-period?${q.toString()}`);
  },
  generate: (period, organizationId) => {
    const q = new URLSearchParams({ period });
    if (organizationId) q.set('organization_id', organizationId);
    return api.post(`/annual-bonus/generate?${q.toString()}`);
  },
  finalize: (id) => api.put(`/annual-bonus/${id}/finalize`),
  applyProrate: (id) => api.put(`/annual-bonus/${id}/apply-prorate`),
  bulkProrate: (period) => api.put(`/annual-bonus/bulk-prorate?${new URLSearchParams({ period }).toString()}`),
  markPaid: (id) => api.put(`/annual-bonus/${id}/pay`),
  updateAmount: (id, amount) => api.put(`/annual-bonus/${id}/update-amount`, { amount }),
  revert: (id) => api.put(`/annual-bonus/${id}/revert`),
  bulkFinalize: (period, orgId) => {
    const q = new URLSearchParams({ period });
    if (orgId) q.set('organization_id', orgId);
    return api.put(`/annual-bonus/bulk-finalize?${q.toString()}`);
  },
  bulkPay: (period, orgId) => {
    const q = new URLSearchParams({ period });
    if (orgId) q.set('organization_id', orgId);
    return api.put(`/annual-bonus/bulk-pay?${q.toString()}`);
  },
  bulkRevert: (period, orgId) => {
    const q = new URLSearchParams({ period });
    if (orgId) q.set('organization_id', orgId);
    return api.put(`/annual-bonus/bulk-revert?${q.toString()}`);
  },
};

// Bonus Payment APIs
export const bonusPaymentAPI = {
  getAll: (params) => api.get('/bonus-payments', { params }),
  create: (data) => api.post('/bonus-payments', data),
  finalize: (id) => api.put(`/bonus-payments/${id}/finalize`),
  markPaid: (id) => api.put(`/bonus-payments/${id}/pay`),
  delete: (id) => api.delete(`/bonus-payments/${id}`),
};

// Export APIs
export const exportAPI = {
  getCSVUrl: (params) => {
    const queryString = new URLSearchParams(params).toString();
    return `${API_BASE}/export/salary-records/csv?${queryString}`;
  },
  getExcelUrl: (params) => {
    const queryString = new URLSearchParams(params).toString();
    return `${API_BASE}/export/salary-records/excel?${queryString}`;
  },
  getRetentionReportUrl: (params) => {
    const queryString = new URLSearchParams(params).toString();
    return `${API_BASE}/export/retention-report?${queryString}`;
  },
  getAnnualBonusReportUrl: (params) => {
    const queryString = new URLSearchParams(params).toString();
    return `${API_BASE}/export/annual-bonus-report?${queryString}`;
  },
};

// User Management APIs
export const userAPI = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  disable: (id) => api.put(`/users/${id}/disable`),
};

// Role APIs
export const roleAPI = {
  getAll: () => api.get('/roles'),
};

// Audit Log APIs
export const auditAPI = {
  getAll: (params) => api.get('/audit-logs', { params }),
  getEntityHistory: (entityType, entityId) => api.get(`/audit-logs/entity/${entityType}/${entityId}`),
};

// Saved Filters APIs
export const savedFiltersAPI = {
  getAll: (page) => api.get('/saved-filters', { params: { page } }),
  create: (data) => api.post('/saved-filters', data),
  update: (id, data) => api.put(`/saved-filters/${id}`, data),
  delete: (id) => api.delete(`/saved-filters/${id}`),
};

// Advance APIs
export const advanceAPI = {
  getAll: (params) => api.get('/advances', { params }),
  getByEmployee: (employeeId, params) => api.get(`/advances/employee/${employeeId}`, { params }),
  create: (data) => api.post('/advances', data),
  update: (id, data) => api.put(`/advances/${id}`, data),
  pause: (id) => api.put(`/advances/${id}/pause`),
  resume: (id) => api.put(`/advances/${id}/resume`),
  convert: (id, data) => api.put(`/advances/${id}/convert`, data),
  delete: (id, force = false) => api.delete(`/advances/${id}`, { params: { force } }),
  getDeductions: (params) => api.get('/advance-deductions', { params }),
};

// Zoho Webhook Integration APIs
export const zohoAPI = {
  // Categories
  getCategories: () => api.get('/zoho/categories'),
  createCategory: (data) => api.post('/zoho/categories', data),
  updateCategory: (id, data) => api.put(`/zoho/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/zoho/categories/${id}`),
  
  // Forms
  getForms: (params) => api.get('/zoho/forms', { params }),
  getForm: (id) => api.get(`/zoho/forms/${id}`),
  createForm: (data) => api.post('/zoho/forms', data),
  updateForm: (id, data) => api.put(`/zoho/forms/${id}`, data),
  deleteForm: (id) => api.delete(`/zoho/forms/${id}`),
  
  // Test mode
  startTest: (formId) => api.post(`/zoho/forms/${formId}/start-test`),
  checkTestStatus: (formId) => api.get(`/zoho/forms/${formId}/test-status`),
  stopTest: (formId) => api.post(`/zoho/forms/${formId}/stop-test`),
  
  // Form-specific data
  getFormSubmissions: (formId, params) => api.get(`/zoho/forms/${formId}/submissions`, { params }),
  getFormLogs: (formId, params) => api.get(`/zoho/forms/${formId}/logs`, { params }),
  
  // Dashboard
  getDashboardStats: (params) => api.get('/zoho/dashboard/stats', { params }),
  getDashboardRecent: (params) => api.get('/zoho/dashboard/recent', { params }),
  getDashboardActivity: (params) => api.get('/zoho/dashboard/activity', { params }),
  
  // Submission actions
  toggleSubmissionTest: (subId) => api.put(`/zoho/submissions/${subId}/toggle-test`),
  approveSubmission: (subId) => api.put(`/zoho/submissions/${subId}/approve`),
  rejectSubmission: (subId) => api.put(`/zoho/submissions/${subId}/reject`),
  updateSubmissionPayload: (subId, payload) => api.put(`/zoho/submissions/${subId}/update-payload`, { raw_payload: payload }),
  deleteSubmission: (subId) => api.delete(`/zoho/submissions/${subId}`),
  toggleLetterSigned: (subId) => api.put(`/zoho/submissions/${subId}/toggle-letter-signed`),
  
  // Accepted submissions for increments
  getAcceptedSubmissions: (params) => api.get('/zoho/accepted-submissions', { params }),
  
  // Auto-detect fields
  autoDetectFields: (formId) => api.post(`/zoho/forms/${formId}/auto-detect-fields`),
  
  // Column config
  saveColumnConfig: (formId, columns) => api.put(`/zoho/forms/${formId}/column-config`, { columns }),
  
  // Report views
  getReportViews: (formId) => api.get(`/zoho/forms/${formId}/report-views`),
  createReportView: (formId, data) => api.post(`/zoho/forms/${formId}/report-views`, data),
  updateReportView: (viewId, data) => api.put(`/zoho/report-views/${viewId}`, data),
  deleteReportView: (viewId) => api.delete(`/zoho/report-views/${viewId}`),
  
  // Smart Dashboard / Entities
  getFormEntities: (formId, params) => api.get(`/zoho/forms/${formId}/entities`, { params }),
  
  // Field Suggestions
  getTargetModules: () => api.get('/zoho/target-modules'),
  getBatchSuggestions: (zohoFields, targetModule) => 
    api.post('/zoho/batch-suggestions', { zoho_fields: zohoFields, target_module: targetModule }),
  
  // Webhook URL helper
  getWebhookUrl: (slug) => `${BACKEND_URL}/api/webhooks/zoho/${slug}`,
};

// Department APIs
export const departmentAPI = {
  getAll: () => api.get('/departments'),
  create: (name) => api.post('/departments', { name }),
  update: (id, name) => api.put(`/departments/${id}`, { name }),
  delete: (id) => api.delete(`/departments/${id}`),
};

// Penalty APIs
export const penaltyAPI = {
  getAll: (params) => api.get('/penalties', { params }),
  getStats: () => api.get('/penalties/stats'),
  create: (data) => api.post('/penalties', data),
  update: (id, data) => api.put(`/penalties/${id}`, data),
  delete: (id) => api.delete(`/penalties/${id}`),
  deduct: (id, month, year, type = 'salary') => api.post(`/penalties/${id}/deduct`, { month, year, type }),
  revertDeduction: (id, reason) => api.post(`/penalties/${id}/revert-deduction`, { reason }),
};

export default api;
